import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, L, CAT_LABEL, STATUS, STATUSES, Badge, fmtDate } from './shared';
import { ConversationThread } from './ConversationThread';

const Section = ({ title, children, testid }) => (
  <div className="mb-4" data-testid={testid}><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{title}</div>{children}</div>
);

const Actions = ({ r, lang, canEdit, onChange }) => {
  const [form, setForm] = useState({ text: '', assignee: '', due_date: '' });
  const add = async () => {
    try { onChange((await axios.post(`${API}/owner-feedback/responses/${r.response_id}/actions`, form)).data); setForm({ text: '', assignee: '', due_date: '' }); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const toggle = async (a) => { try { onChange((await axios.patch(`${API}/owner-feedback/responses/${r.response_id}/actions/${a.action_id}`, { done: !a.done })).data); } catch (e) { toast.error('Error'); } };
  return (
    <Section title={L(lang, 'Ενέργειες που έγιναν / προγραμματίστηκαν', 'Actions taken / planned')} testid="of-followup-actions">
      {(r.followup?.actions || []).map((a) => (
        <label key={a.action_id} className="flex items-start gap-2 text-sm py-0.5" data-testid={`of-action-${a.action_id}`}>
          <input type="checkbox" disabled={!canEdit} checked={a.done} onChange={() => toggle(a)} className="mt-1" />
          <span className={a.done ? 'line-through text-zinc-400' : ''}>{a.text}<span className="text-xs text-zinc-500"> {a.assignee && `· ${a.assignee}`} {a.due_date && `· ${fmtDate(a.due_date)}`}</span></span>
        </label>))}
      {canEdit && (
        <div className="flex flex-wrap gap-1 mt-1">
          <Input className="h-8 rounded-none flex-1 min-w-[180px]" placeholder={L(lang, 'Νέα ενέργεια', 'New action')} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="of-action-text" />
          <Input className="h-8 rounded-none w-36" placeholder={L(lang, 'Υπεύθυνος', 'Assignee')} value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} data-testid="of-action-assignee" />
          <Input type="date" className="h-8 rounded-none w-36" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="of-action-due" />
          <Button size="sm" className="h-8 rounded-none" disabled={!form.text.trim()} onClick={add} data-testid="of-action-add-btn">+</Button>
        </div>)}
    </Section>
  );
};

const AddBox = ({ r, lang, canEdit, onChange, kind }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const isReply = kind === 'reply';
  const add = async () => {
    setBusy(true);
    try {
      const res = (await axios.post(`${API}/owner-feedback/responses/${r.response_id}/${isReply ? 'reply' : 'notes'}`, { text })).data;
      onChange(res); setText('');
      if (isReply) {
        const st = res.owner_replies?.[res.owner_replies.length - 1]?.email_status || '';
        if (st.startsWith('failed')) toast.error(st); else toast.success(L(lang, 'Η απάντηση στάλθηκε στον ιδιοκτήτη', 'Reply sent to owner'));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  const list = isReply ? (r.owner_replies || []) : (r.followup?.notes || []);
  return (
    <Section testid={isReply ? 'of-owner-replies' : 'of-followup-notes'}
      title={isReply ? L(lang, 'Συνομιλία με τον ιδιοκτήτη (οι απαντήσεις στέλνονται με email και σύνδεσμο)', 'Conversation with owner (replies are emailed with a link)') : L(lang, 'Εσωτερικές σημειώσεις — ΔΕΝ τις βλέπει ποτέ ο ιδιοκτήτης', 'Internal notes — NEVER visible to the owner')}>
      {list.map((n) => <div key={n.note_id} className="text-sm border-l-2 pl-2 mb-1 border-zinc-300"><div className="whitespace-pre-wrap">{n.text}</div><div className="text-[10px] text-zinc-400">{n.by} · {fmtDate(n.at)}</div></div>)}
      {canEdit && <div className="flex gap-1 mt-1"><Textarea rows={2} className="rounded-none" value={text} onChange={(e) => setText(e.target.value)} data-testid={isReply ? 'of-reply-text' : 'of-note-text'} />
        <Button className="rounded-none" disabled={!text.trim() || busy} onClick={add} data-testid={isReply ? 'of-reply-send-btn' : 'of-note-add-btn'}>{isReply ? L(lang, 'Αποστολή', 'Send') : L(lang, 'Προσθήκη', 'Add')}</Button></div>}
    </Section>
  );
};

export const OwnerFeedbackResponseDialog = ({ response, onClose, onChange, canEdit, lang }) => {
  if (!response) return null;
  const r = response;
  const setStatus = async (status) => {
    try { onChange((await axios.patch(`${API}/owner-feedback/responses/${r.response_id}/status`, { status })).data); toast.success(L(lang, 'Η κατάσταση ενημερώθηκε', 'Status updated')); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="of-response-dialog">
        <DialogHeader><DialogTitle>{r.building_name} · {L(lang, 'Διαμ.', 'Apt')} {r.apartment_number} {r.is_test && <span className="ml-2 text-[10px] bg-zinc-800 text-white px-1.5 py-0.5">TEST</span>}</DialogTitle></DialogHeader>
        <div className="text-xs text-zinc-500 mb-3">{r.owner_name || '—'} · {fmtDate(r.submitted_at)} · {r.wants_response ? L(lang, 'Ζητά απάντηση', 'Wants a response') : L(lang, 'Δεν ζητά απάντηση', 'No response requested')}</div>
        <Section title={L(lang, 'Κατηγορία', 'Category')}><p className="text-sm font-semibold" data-testid="of-dialog-category">{CAT_LABEL[lang][r.category] || r.category}</p></Section>
        {r.invitation?.title && <Section title={L(lang, 'Τίτλος αιτήματος', 'Request title')}><p className="text-sm">{r.invitation.title}</p></Section>}
        {r.needs_action && <div className="mb-3 px-2 py-1.5 bg-orange-50 border border-orange-300 text-orange-800 text-xs font-semibold" data-testid="of-dialog-needs-action">{L(lang, 'Απαιτείται ενέργεια διαχείρισης', 'Management action required')}{r.owner_waiting_since ? ` · ${L(lang, 'ο ιδιοκτήτης περιμένει από', 'owner waiting since')} ${new Date(r.owner_waiting_since).toLocaleString('el-GR')}` : ''}</div>}
        <ConversationThread r={r} lang={lang} canEdit={canEdit} onChange={onChange} />
        <div className="border-t border-zinc-200 pt-3">
          <Section title={L(lang, 'Κατάσταση', 'Status')}>
            <div className="flex items-center gap-2">
              <Badge map={STATUS} value={r.status} lang={lang} testid="of-dialog-status-badge" />
              <select className="border border-zinc-300 h-8 px-2 text-sm" disabled={!canEdit} value={r.status} onChange={(e) => setStatus(e.target.value)} data-testid="of-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS[s][lang]}</option>)}
              </select>
            </div>
          </Section>
          <Actions r={r} lang={lang} canEdit={canEdit} onChange={onChange} />
          <AddBox r={r} lang={lang} canEdit={canEdit} onChange={onChange} kind="note" />
          <Section title={L(lang, 'Ιστορικό', 'History')} testid="of-dialog-history">
            {(r.followup?.history || []).map((h, i) => <div key={i} className="text-[11px] text-zinc-500">{fmtDate(h.at)} · {h.by} · {h.change?.from ? `${STATUS[h.change.from]?.[lang] || h.change.from} → ` : ''}{STATUS[h.change?.status]?.[lang] || JSON.stringify(h.change)}</div>)}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OwnerFeedbackResponseDialog;
